import * as React from "react";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CardBase, GlassTable } from "../../../components/global";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { Talent } from "../../../types/users";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import TalentDrawer from "../../talent/TalentDrawer";
import {
  useAcceptBidMutation,
  useHireTalentMutation,
} from "../../../services/projectJob.service";
import dayjs from "dayjs";
import {
  NegotiationStatus,
  Project,
  ProjectResource,
  ProjectStatus,
} from "../../../types/project";
import { getTalentBidStatus } from "../../../utils/projectHelper";
import ModalInvite from "../ModalInvite";
import { formatWithTimezone } from "../../../utils/dateHelper";

const TableInvites = ({
  project,
  bids,
}: {
  project: Project;
  bids: Array<ProjectResource>;
}) => {
  const invitesJob = bids.filter((bid) => bid.status === "invited");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const user = useCurrentUser();
  const [selectedTalent, setSelectedTalent] = React.useState<Talent | null>(
    null
  );
  const [hireModal, setHireModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [showModalInvite, setShowModalInvite] = React.useState(false);
  const [showFav, setShowFav] = React.useState(false);

  const [showTable, setShowTable] = React.useState(
    project.status === ProjectStatus.PUBLISHED
  );
  const isHourly = React.useMemo(() => project?.type === "hourly", [project]);

  const theme = useTheme();
  const borderColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.08)";
  const headerGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`;
  const rowHoverBg =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.16)
      : alpha(theme.palette.primary.main, 0.08);
  const subtleTextColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.7)"
      : "rgba(0, 0, 0, 0.6)";
  const isDark = theme.palette.mode === "dark";
  const headCellSx = {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: { xs: "0.6rem", sm: "0.75rem" },
    letterSpacing: { xs: "0.04em", sm: "0.08em" },
    borderBottom: "none",
    color: "#12903C",
    py: { xs: 0.75, sm: 2 },
    px: { xs: 0.5, sm: 2 },
    lineHeight: { xs: 1.2, sm: 1.5 },
    whiteSpace: { xs: "normal", sm: "nowrap" },
    wordBreak: { xs: "break-word", sm: "normal" },
    overflow: { xs: "hidden", sm: "visible" },
    textOverflow: { xs: "ellipsis", sm: "clip" },
  } as const;

  const inviteCount = React.useMemo(() => invitesJob?.length ?? 0, [invitesJob]);

  const [doHire] = useHireTalentMutation();
  const [doAccept] = useAcceptBidMutation();

  const isAcceptingBid = React.useMemo(
    () => project.status === ProjectStatus.PUBLISHED,
    [project]
  );

  return (
    <Box>
      <CardBase
        title="Invites"
        subtitle={
          inviteCount > 0
            ? `${inviteCount} invite${inviteCount === 1 ? "" : "s"} sent`
            : "No invites sent yet"
        }
        useGlassCard={true}
        nested={true}
        headerActions={
          <IconButton
            onClick={() => setShowTable(!showTable)}
            sx={{
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: rowHoverBg,
              },
            }}
          >
            {showTable ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        sx={{
          width: "100%",
          p: 0,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 18px 48px rgba(0, 0, 0, 0.5)"
                : "0 18px 48px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {showTable && (
          <Box sx={{ px: 0, py: { xs: 0, md: 3 } }}>
            <GlassTable maxHeight={{ xs: 420, md: 560 }} hasData={inviteCount > 0}>
              <Table
                stickyHeader
                aria-label="invites"
                sx={{
                  minWidth: { xs: 300, sm: 650 },
                  width: "100%",
                  "& .MuiTableHead-root .MuiTableRow-root": {
                    background: headerGradient,
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    ...headCellSx,
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderBottom: `1px solid ${borderColor}`,
                    fontSize: { xs: "0.7rem", sm: "0.95rem" },
                    color: theme.palette.text.primary,
                    py: { xs: 1, sm: 2 },
                    px: { xs: 0.5, sm: 2 },
                    verticalAlign: "top",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: rowHoverBg,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      maxWidth: { xs: 140, sm: "none" },
                      minWidth: { xs: 120, sm: "auto" }
                    }}>
                      Independent Professional
                    </TableCell>
                    <TableCell align="center" sx={{
                      width: { xs: 70, sm: "auto" },
                      minWidth: { xs: 60, sm: "auto" }
                    }}>
                      Price
                    </TableCell>
                    {isHourly && (
                      <TableCell align="center" sx={{
                        width: { xs: 70, sm: "auto" },
                        minWidth: { xs: 60, sm: "auto" }
                      }}>
                        Max Hour
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{
                      maxWidth: { xs: 200, sm: "none" },
                      minWidth: { xs: 100, sm: "auto" }
                    }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{
                      width: { xs: 100, sm: "auto" },
                      minWidth: { xs: 90, sm: "auto" }
                    }}>
                      Updated
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inviteCount === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={isHourly ? 6 : 5}
                        sx={{
                          textAlign: "center",
                          py: { xs: 4, sm: 6 },
                          color: subtleTextColor,
                          fontSize: { xs: "0.7rem", sm: "0.95rem" },
                        }}
                      >
                        No invites sent yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {invitesJob?.map((job) => {
                    return (
                      <TableRow
                        key={job.id}
                        sx={{
                          "&:last-child .MuiTableCell-root": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell sx={{ width: { xs: 140, md: 260 } }}>
                          <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} alignItems="center">
                            <Box>
                              <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" flexWrap="wrap">
                                <Link
                                  component="button"
                                  onClick={() => {
                                    setSelectedTalent(job.resource || null);
                                    setDrawerOpen(true);
                                  }}
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "1rem" },
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {job.resource?.full_name || "N/A"}
                                </Link>
                                <Rating
                                  value={job.resource?.total_rating ?? 0}
                                  readOnly
                                  precision={0.5}
                                  size="small"
                                  sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                                />
                              </Stack>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: subtleTextColor,
                                  display: "block",
                                  mt: 0.25,
                                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                }}
                              >
                                {[
                                  job.resource?.city,
                                  job.resource?.state,
                                  job.resource?.zip
                                ].filter(Boolean).join(", ") || "Location not available"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                          {job.status === NegotiationStatus.INVITED
                            ? "n/a"
                            : isHourly
                              ? job.rate !== null ? `$${job.rate}/hr` : "-"
                              : job.price !== null ? `$${job.price}` : "-"}
                        </TableCell>
                        {isHourly && (
                          <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                            {job.max_hour !== null ? `${job.max_hour} hr` : "-"}
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Chip
                            label="INVITED"
                            color="info"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: "8px",
                              px: 0.75,
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              height: { xs: 20, sm: 24 },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              color: subtleTextColor,
                            }}
                          >
                            {job.updated_at ? dayjs(job.updated_at).format("MMM DD, YYYY hh:mm A z") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </GlassTable>
          </Box>
        )}
      </CardBase>
      <TalentDrawer
        forceRefresh={true}
        data={selectedTalent}
        open={drawerOpen}
        setOpen={setDrawerOpen}
      />

      <ModalInvite
        open={showModalInvite}
        onClose={() => {
          setShowFav(false);
          setShowModalInvite(false);
        }}
        project={project}
        showFav={showFav}
        setShowFav={setShowFav}
      />
    </Box>
  );
};
export default TableInvites;
